import { Link } from 'react-router-dom';

interface Feature {
  icon: string;
  title: string;
  description: string;
  color: string;
  imageUrl: string;
}

const features: Feature[] = [
  {
    icon: 'ri-star-line',
    title: 'BaseTao Review',
    description: 'Real experience analysis, pros and cons, and honest alternatives compared side by side.',
    color: 'accent',
    imageUrl: 'https://readdy.ai/api/search-image?query=Premium%20quality%20fashion%20items%20laid%20out%20for%20review%20inspection%20on%20clean%20white%20surface%2C%20luxury%20sneakers%20and%20accessories%2C%20soft%20natural%20lighting%2C%20warm%20cream%20tones%2C%20editorial%20product%20photography%20style%2C%20minimalist%20composition%2C%20high-end%20aesthetic&width=600&height=340&seq=feature-review-01&orientation=landscape',
  },
  {
    icon: 'ri-ship-line',
    title: 'BaseTao Shipping Cost',
    description: 'Shipping methods, estimated costs, and optimization strategies to save on every order.',
    color: 'primary',
    imageUrl: 'https://readdy.ai/api/search-image?query=International%20shipping%20concept%20with%20globe%20and%20parcel%20boxes%20on%20modern%20desk%2C%20clean%20minimalist%20workspace%2C%20warm%20neutral%20tones%2C%20soft%20studio%20lighting%2C%20editorial%20logistics%20photography%20style%2C%20premium%20aesthetic%2C%20professional%20scene&width=600&height=340&seq=feature-shipping-02&orientation=landscape',
  },
  {
    icon: 'ri-coupon-line',
    title: 'BaseTao Coupons',
    description: 'Latest discount opportunities, promo codes, and proven saving methods for smart shoppers.',
    color: 'secondary',
    imageUrl: 'https://readdy.ai/api/search-image?query=Discount%20coupon%20tags%20and%20sale%20ribbons%20arranged%20artistically%20on%20marble%20surface%2C%20luxury%20shopping%20promotion%20concept%2C%20warm%20cream%20and%20gold%20accents%2C%20soft%20natural%20lighting%2C%20editorial%20product%20photography%2C%20clean%20minimalist%20composition&width=600&height=340&seq=feature-coupon-03&orientation=landscape',
  },
  {
    icon: 'ri-store-2-line',
    title: 'BaseTao Warehouse',
    description: 'How warehouse storage, QC photos, and parcel consolidation work behind the scenes.',
    color: 'accent',
    imageUrl: 'https://readdy.ai/api/search-image?query=Modern%20organized%20warehouse%20shelves%20with%20neatly%20stacked%20packages%20and%20fashion%20boxes%2C%20warm%20industrial%20interior%20lighting%2C%20concrete%20and%20wood%20elements%2C%20clean%20logistics%20scene%2C%20editorial%20photography%20style%2C%20premium%20aesthetic%2C%20soft%20ambient%20atmosphere&width=600&height=340&seq=feature-warehouse-04&orientation=landscape',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Legitimacy Check',
    description: 'Safety analysis, trust evaluation, and everything you need to verify before purchasing.',
    color: 'primary',
    imageUrl: 'https://readdy.ai/api/search-image?query=Secure%20online%20shopping%20concept%20with%20laptop%20displaying%20trust%20badge%20and%20credit%20card%20on%20desk%2C%20modern%20minimalist%20setting%2C%20warm%20neutral%20tones%2C%20soft%20natural%20daylight%2C%20editorial%20photography%20style%2C%20clean%20composition%2C%20premium%20aesthetic&width=600&height=340&seq=feature-security-05&orientation=landscape',
  },
];

const colorMap: Record<string, { iconBg: string }> = {
  accent: { iconBg: 'bg-accent-100 text-accent-600' },
  primary: { iconBg: 'bg-primary-100 text-primary-600' },
  secondary: { iconBg: 'bg-secondary-100 text-secondary-600' },
};

export default function FeatureCards() {
  return (
    <section id="guides" className="relative py-16 md:py-20 bg-background-100">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <span className="inline-block px-3 py-1 rounded-full bg-accent-50 text-accent-700 text-xs font-semibold uppercase tracking-wider mb-4">
            Research Hub
          </span>
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
            What Is <span className="text-primary-600">BaseTao Spreadsheet</span>?
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
            BaseTao Spreadsheet is a structured research resource designed to help users organize product information, compare options, track purchases, analyze shipping costs, and make smarter decisions — all in one place.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {features.map((feature) => {
            const colors = colorMap[feature.color];
            return (
              <div
                key={feature.title}
                className="group bg-background-50 rounded-2xl overflow-hidden hover:scale-[1.02] transition-all duration-300 border border-background-200/60 cursor-pointer"
              >
                {/* Feature Image */}
                <div className="relative h-44 md:h-48 overflow-hidden">
                  <img
                    src={feature.imageUrl}
                    alt={feature.title}
                    title={feature.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                </div>

                <div className="p-5 md:p-6">
                  <div className={`w-10 h-10 rounded-lg ${colors.iconBg} flex items-center justify-center mb-3`}>
                    <i className={`${feature.icon} text-lg`}></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA to article page */}
        <div className="text-center mt-10">
          <Link
            to="/basetao-spreadsheet-guide"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 font-semibold text-sm rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-article-line"></i>
            Read the Full BaseTao Spreadsheet Guide
          </Link>
        </div>
      </div>
    </section>
  );
}